import { WarehouseUtilization } from '../../types';

interface WarehouseUtilizationBarProps {
  utilization: WarehouseUtilization | null;
  isLoading?: boolean;
  className?: string;
  /** Slim, low-height layout for use inside forms. */
  compact?: boolean;
}

export default function WarehouseUtilizationBar({
  utilization,
  isLoading = false,
  className = '',
  compact = false,
}: WarehouseUtilizationBarProps) {
  if (isLoading) {
    return (
      <div className={`bg-gradient-to-br from-brand-50 to-brand-50 border-2 border-brand-200 rounded-xl ${compact ? 'p-3' : 'p-4 sm:p-6'} shadow-lg ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mr-3"></div>
          <span className="text-gray-600 font-medium text-sm sm:text-base">Loading warehouse utilization...</span>
        </div>
      </div>
    );
  }

  if (!utilization || !utilization.warehouseId) {
    return (
      <div className={`bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 sm:p-6 shadow-lg ${className}`}>
        <div className="flex items-start">
          <svg className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-yellow-900 mb-1">No Active Warehouse</h4>
            <p className="text-sm text-yellow-800">
              Please configure a warehouse before managing client space allocations.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { warehouseName, totalCapacitySqft, allocatedSqft, availableSqft, utilizationPercentage, clientCount } = utilization;

  const barColor =
    utilizationPercentage >= 90
      ? 'bg-gradient-to-r from-red-500 to-red-600'
      : utilizationPercentage >= 70
      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
      : 'bg-gradient-to-r from-green-500 to-green-600';
  const statusBadge =
    utilizationPercentage >= 90
      ? 'bg-red-100 text-red-700'
      : utilizationPercentage >= 70
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-green-100 text-green-700';
  const statusLabel = utilizationPercentage >= 90 ? 'Critical' : utilizationPercentage >= 70 ? 'High' : 'Good';

  // Compact layout for forms — single low-height card
  if (compact) {
    return (
      <div className={`bg-gradient-to-br from-brand-50 to-brand-50 border border-brand-200 rounded-xl shadow-sm px-4 py-3 ${className}`}>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-ink-900 truncate">Warehouse Space Utilization</h3>
              <p className="text-xs text-ink-500 truncate">{warehouseName} · {clientCount} active clients</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] font-semibold text-ink-500 uppercase leading-none">Total</p>
              <p className="text-sm font-bold text-ink-900 tabular-nums">{totalCapacitySqft.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold text-ink-500 uppercase leading-none">Allocated</p>
              <p className="text-sm font-bold text-orange-600 tabular-nums">{allocatedSqft.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold text-ink-500 uppercase leading-none">Available</p>
              <p className="text-sm font-bold text-green-600 tabular-nums">{availableSqft.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-3">
          <div className="relative flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ${barColor}`}
              style={{ width: `${Math.min(utilizationPercentage, 100)}%` }}
            />
          </div>
          <span className="text-xs font-bold text-ink-900 tabular-nums">{utilizationPercentage}%</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadge}`}>{statusLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-brand-50 to-brand-50 border-2 border-brand-200 rounded-xl shadow-lg overflow-hidden max-w-full ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-600 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-white truncate">Warehouse Space Utilization</h3>
              <p className="text-sm text-brand-100 truncate">{warehouseName}</p>
            </div>
          </div>
          <div className="flex items-center justify-between sm:block sm:text-right flex-shrink-0">
            <p className="text-sm text-brand-100">Active Clients</p>
            <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">{clientCount}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-4 sm:px-6">
        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Total Capacity */}
          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border-2 border-brand-100">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">Total Capacity</h4>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 tabular-nums">{totalCapacitySqft.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">sqft</p>
          </div>

          {/* Allocated */}
          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border-2 border-orange-100">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">Allocated</h4>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-orange-600 tabular-nums">{allocatedSqft.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">sqft to clients</p>
          </div>

          {/* Available */}
          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border-2 border-green-100">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">Available</h4>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-green-600 tabular-nums">{availableSqft.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">sqft remaining</p>
          </div>
        </div>

        {/* Utilization Bar */}
        <div className="bg-white rounded-lg p-3 sm:p-5 shadow-sm border-2 border-brand-100 overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h4 className="text-sm font-semibold text-gray-700 truncate">Space Utilization</h4>
            <div className="flex items-center flex-shrink-0">
              <span className="text-xl sm:text-2xl font-bold text-gray-900 mr-2 tabular-nums">{utilizationPercentage}%</span>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge}`}>
                {statusLabel}
              </span>
            </div>
          </div>

          {/* Progress Bar — label below so it never clips inside a thin fill */}
          <div className="relative w-full bg-gray-200 rounded-full h-3 sm:h-4 overflow-hidden shadow-inner">
            <div
              className={`h-full rounded-full transition-all duration-500 ${barColor}`}
              style={{ width: `${Math.min(utilizationPercentage, 100)}%` }}
            />
          </div>
          <p className="text-xs font-semibold text-gray-600 mt-1.5 tabular-nums">
            {allocatedSqft.toLocaleString()} / {totalCapacitySqft.toLocaleString()} sqft
          </p>

          {/* Status Message */}
          <div className="mt-3 flex items-start">
            <svg className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${
              utilizationPercentage >= 90
                ? 'text-red-600'
                : utilizationPercentage >= 70
                ? 'text-yellow-600'
                : 'text-green-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className={`text-xs font-medium ${
              utilizationPercentage >= 90
                ? 'text-red-700'
                : utilizationPercentage >= 70
                ? 'text-yellow-700'
                : 'text-green-700'
            }`}>
              {utilizationPercentage >= 90
                ? 'Critical: Warehouse is nearly full. Consider expanding capacity or reviewing allocations.'
                : utilizationPercentage >= 70
                ? 'Warning: Warehouse is filling up. Monitor space allocation closely.'
                : 'Good: Adequate space available for new client allocations.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
